import { FunctionComponent, useState, useEffect, useRef, useMemo } from 'react';
import { Alert, Paper, Box, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Button, List, ListItem, ListItemText, ClickAwayListener, Tooltip } from '@mui/material';
import DashboardLayout from '../../../components/layout/layout';
import { styled } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SearchInventoryIcon from '../../../assets/img/inventory_black.png';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useGetLeadTimeListPrice, useGetInventoryAutocomplete } from '../../../hooks/api/user';
//import RecentInventorySearch from '../../../components/common/recent-inventory-search';
import axios from 'axios';
import { StyledTextField } from '../../../components/ui/formInput';
import { useQueryClient } from '@tanstack/react-query';

const TableCol = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(224, 224, 224, 1)',
}));

export interface inventory {
  userId: number;
  Date: string;
  Instock: string;
  Company: string;
  Details: string;
}

type LeadTimeListPriceProps = {
  LeadTime: string;
  EXCOO: string;
  currentDatePST: number;
  description2: string;
  TCode: string;
  listPrice: string;
  partNo: string;
  description1: string;
  OrderCap: string;
  inStock: string;
  listPriceMessage:string;
  MOQ?: number;
}

type SortField = 'partNo' | 'listPrice' | 'LeadTime' | 'description1' | 'inStock' | 'TCode' | 'EXCOO';
type SortDirection = 'asc' | 'desc' | null;



const LeadTimeSearchResult: FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const partNo = queryParams.get('partNo') || '';
  const [showAutocomplete, setShowAutocomplete] = useState<boolean>(false);
  const [debouncedPrefix, setDebouncedPrefix] = useState<string>('');
  const autocompleteRef = useRef<HTMLDivElement>(null);
  
  const { data: leadTimeListPrice, isFetched: hasFetchedLeadTime, error } = useGetLeadTimeListPrice({ partNo });
  //const [showPartNoColumn, setShowPartNoColumn] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isObsoletePart, setIsObsoletePart] = useState<boolean>(false);
  const [obsoletePartData, setObsoletePartData] = useState<{
    header?: string;
    crossReferenceLink?: string;
    crossReferenceText?: string;
    contactInfo?: string;
  } | null>(null);
  const [searchPartNumber, setSearchPartNumber] = useState<string>(partNo);
  const [validateFields, setValidateFields] = useState<{ partNumber: { invalid: boolean; error: string } }>({ partNumber: { invalid: false, error: '' } });

  // Autocomplete query - only when user is actively typing (searchPartNumber differs from partNo)
  const { data: autocompleteData, isLoading: isAutocompleteLoading } = useGetInventoryAutocomplete(
    searchPartNumber !== partNo ? debouncedPrefix : '' // Only search when user is typing something different
  );
  const suggestions = autocompleteData?.partNumbers || [];

  // Get all lead time data without pagination
  const allLeadTimeData = useMemo(() => {
    return leadTimeListPrice?.content || [];
  }, [leadTimeListPrice?.content]);

  // Sorting state (pagination removed)
  const [sortField, setSortField] = useState<SortField>('partNo');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Sort all data client-side
  const sortedData = useMemo(() => {
    if (!allLeadTimeData.length) return [];
    
    return [...allLeadTimeData].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle number sorting for LeadTime (extract numeric value)
      if (sortField === 'LeadTime') {
        const aNum = parseInt(aValue) || 0;
        const bNum = parseInt(bValue) || 0;
        aValue = aNum;
        bValue = bNum;
      }
      // Handle number sorting for listPrice (extract numeric value from currency string)
      else if (sortField === 'listPrice') {
        const aNum = parseFloat(aValue?.replace(/[^0-9.]/g, '')) || 0;
        const bNum = parseFloat(bValue?.replace(/[^0-9.]/g, '')) || 0;
        aValue = aNum;
        bValue = bNum;
      }
      // Handle string sorting (case-insensitive)
      else {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [allLeadTimeData, sortField, sortDirection]);

  // Use all sorted data (no pagination)
  const displayData = useMemo(() => {
    return sortedData;
  }, [sortedData]);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatListPriceMessage = (message: string) => {
  // Check if message contains "Check Price Sheets"
  if (message.includes("Check Price Sheets")) {
    // Split the message around "Check Price Sheets"
    const parts = message.split("Check Price Sheets");
    const before = parts[0];
    const after = parts[1] || '';
    
    return (
      <span>
        {before}
        <Box
          component="span"
          onClick={() => navigate('/resources/resource-details/15', { state: { categoryName: 'Price Sheets' } })}
          sx={{
            textDecoration: 'none',
            color: '#1976d2',
            fontWeight: 400,
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
              color: '#1565c0',
            },
          }}
        >
          Check Price Sheets
        </Box>
        {after}
      </span>
    );
  }
  
  // fallback: auto-link URLs
  const urlRegex = /(https?:\/\/[^\s)]+)/g;
  const parts = message.split(urlRegex);
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1976d2', textDecoration: 'underline' }}
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

  useEffect(() => {
    if (error) {
      if (axios.isAxiosError(error) && error.response && error.response.data) {
        const errData = error.response.data as { 
          message?: string; 
          error?: string; 
          header?: string;
          crossReferenceLink?: string;
          crossReferenceText?: string;
          contactInfo?: string;
        };
        
        // Check if this is an obsolete part error or gone part error (both show same message)
        if (errData.error === 'Obsolete Part' || errData.error === 'Gone Part') {
          setIsObsoletePart(true);
          // For Gone Part, use the same message as Obsolete Part
          setErrorMessage('The part number you entered is obsolete.');
          setObsoletePartData({
            header: 'The part number you entered is obsolete.',
            crossReferenceLink: errData.crossReferenceLink || '/resources/resource-details/21',
            crossReferenceText: errData.crossReferenceText || 'Check the product cross reference sheets to see if a replacement is available.',
            contactInfo: 'If further assistance is needed please email the team at orders@barksdale.com'
          });
        } else {
          setIsObsoletePart(false);
          setObsoletePartData(null);
          const combinedMessage = `${errData.error || 'Error'}: ${errData.message || ''}`;
          setErrorMessage(combinedMessage);
        }
      } else if (typeof error === 'string') {
        setIsObsoletePart(false);
        setErrorMessage(error);
      } else {
        setIsObsoletePart(false);
        setErrorMessage('An unknown error occurred');
      }
    } else {
      setErrorMessage(null);
      setIsObsoletePart(false);
    }
    
    // const hasWildcard = partNo.includes('%') || partNo.includes('_');
    // setShowPartNoColumn(hasWildcard);
  }, [leadTimeListPrice, error, partNo]);

  useEffect(() => {
    setSearchPartNumber(partNo);
  }, [partNo]);

  // Debounce effect for autocomplete - only when user is actively typing
  useEffect(() => {
    // Don't show autocomplete if user is not typing something different
    if (searchPartNumber === partNo) {
      setShowAutocomplete(false);
      return;
    }
    
    const timer = setTimeout(() => {
      if (searchPartNumber.trim().length >= 2) {
        setDebouncedPrefix(searchPartNumber.trim());
        setShowAutocomplete(true);
      } else {
        setShowAutocomplete(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchPartNumber, partNo]);

  // Handle suggestion selection - auto-search
  const handleSuggestionClick = (suggestion: string) => {
    setSearchPartNumber(suggestion);
    setShowAutocomplete(false);
    // Auto-trigger search
    setTimeout(() => {
      navigate(`/inventory/lead-time/search-result?partNo=${encodeURIComponent(suggestion)}`);
    }, 100);
  };

  // Handle input change
  const handleSearchPartNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchPartNumber(value);
    setValidateFields({ partNumber: { invalid: false, error: '' } }); // Clear validation errors
  };

  // Handle click away from autocomplete
  const handleClickAway = () => {
    setShowAutocomplete(false);
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };
    if (!searchPartNumber?.trim()) {
      errors.partNumber = { invalid: true, error: 'Please enter a part number.' };
      valid = false;
    } else {
      errors.partNumber = { invalid: false, error: '' };
    }
    setValidateFields(errors);
    return valid;
  };

  const handleSearch = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    setValidateFields({ partNumber: { invalid: false, error: '' } });
    if (!validateForm()) return;
    const trimmed = searchPartNumber.trim();
    navigate(`/inventory/lead-time/search-result?partNo=${encodeURIComponent(trimmed)}`);
  };

  const queryClient = useQueryClient();

  const handleBack = () => {
    queryClient.clear();
    navigate('/inventory');
  };

  // Get sort icon for a column
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <UnfoldMoreIcon fontSize="small" />;
    }
    if (sortDirection === 'asc') {
      return <ArrowUpwardIcon fontSize="small" />;
    }
    if (sortDirection === 'desc') {
      return <ArrowDownwardIcon fontSize="small" />;
    }
    return <UnfoldMoreIcon fontSize="small" />;
  };

const isExactSearch = (
  partNo: string,
  content: LeadTimeListPriceProps[] | undefined
): boolean => {
  if (!partNo || !content || content.length !== 1) return false;
  return content[0].partNo?.toLowerCase() === partNo.toLowerCase();
};


  return (
    <>
      <DashboardLayout 
          titleIcon={<img src={SearchInventoryIcon} alt="Search Inventory" 
          style={{ width: 25, height: 25 }} />} title={"Inventory"} 
          initialBreadcrumbs={"Channel Partners/ Inventory"} 
          levelBreadcrumbs={''} 
          breadcrumbs={' List Price Lead Time Search'} 
          activeMenu={'inventory'}
      >
        <Box sx={{ width: '100%', maxWidth: '100%',
          backgroundColor: 'white',
          borderRadius: 1,
          border: '1px solid rgba(224, 224, 224, 1)',
        }}>
          {errorMessage && (
            <Box sx={{ mt: 2 }}>
                              {isObsoletePart ? (
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: '#f8f9fa', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#495057' }}>
                      {obsoletePartData?.header || 'The part number you entered is obsolete.'}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 3, color: '#6c757d', lineHeight: 1.6 }}>
                      <Box
                        component="span"
                        onClick={() => navigate(obsoletePartData?.crossReferenceLink || '/resources/resource-details/21', { state: { categoryName: 'Competitor Cross References' } })}
                        sx={{
                          textDecoration: 'none',
                          color: '#1976d2',
                          fontWeight: 400,
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: '#1565c0',
                          },
                        }}
                      >
                        {obsoletePartData?.crossReferenceText || 'Check the product cross reference sheets to see if a replacement is available.'}
                      </Box>
                    </Typography>
                    
                    <Typography variant="body1" sx={{ color: '#6c757d', lineHeight: 1.6 }}>
                      If further assistance is needed please email the team at{' '}
                      <a 
                        href="mailto:orders@barksdale.com" 
                        style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        orders@barksdale.com
                      </a>
                    </Typography>
                  </Box>
              ) : (
                <Alert severity="error" style={{ whiteSpace: 'pre-line' }}>
                  {errorMessage}
                </Alert>
              )}
            </Box>
          )}    

        <form onSubmit={handleSearch}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, mb: 2, ml: 2, position: 'relative' }}>
            <Box sx={{ position: 'relative', flex: 1 }}>
              <StyledTextField
                label="Enter Part Number"
                value={searchPartNumber}
                onChange={handleSearchPartNumberChange}
                error={validateFields.partNumber.invalid}
                helperText={validateFields.partNumber.error}
              />
              {showAutocomplete && (
                <ClickAwayListener onClickAway={handleClickAway}>
                  <Paper
                    ref={autocompleteRef}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      maxHeight: 200,
                      overflow: 'auto',
                      boxShadow: 3,
                      border: '1px solid #ddd',
                      mt: 0.5
                    }}
                  >
                    <List dense>
                      {isAutocompleteLoading ? (
                        <ListItem>
                          <ListItemText primary="Loading..." />
                        </ListItem>
                      ) : suggestions.length > 0 ? (
                        suggestions.map((suggestion: string, index: number) => (
                          <ListItem
                            key={index}
                            component="div"
                            onClick={() => handleSuggestionClick(suggestion)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: '#f5f5f5'
                              }
                            }}
                          >
                            <ListItemText primary={suggestion} />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText primary="No suggestions found" />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </ClickAwayListener>
              )}
            </Box>
            <Button
              variant="contained"
              sx={{ backgroundColor: '#c3183c', color: 'white', borderRadius: '6px', px: 4, '&:hover': { backgroundColor: '#a91532' }, height: '40px' }}
              type="submit"
            >
              SEARCH
            </Button>
          </Box>
        </form>  

          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0, mt: 3 }}>
            <Table size="small" aria-label="inventory list table">
              <TableHead sx={{ backgroundColor: "#c3183c" }}>
                <TableRow>
                  {/* <TableCell colSpan={showPartNoColumn ? 7 : 6} sx={{ color: 'white', backgroundColor: '#c3183c', p: 1 }}> */}
                  <TableCell colSpan={7} sx={{ color: 'white', backgroundColor: '#c3183c', p: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        Barksdale Part Number: {partNo}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          Results: {allLeadTimeData.length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          Information current as of {new Date().toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  </TableRow>
              </TableHead>
              <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                <TableRow>
                  {/* {showPartNoColumn && <TableCol>Part No</TableCol>} */}
                  <TableCol 
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { backgroundColor: '#e0e0e0' },
                      userSelect: 'none'
                    }}
                    onClick={() => handleSort('partNo')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Part No
                      <Tooltip title="Click to sort">
                        {getSortIcon('partNo')}
                      </Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol 
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { backgroundColor: '#e0e0e0' },
                      userSelect: 'none'
                    }}
                    onClick={() => handleSort('listPrice')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      List Price
                      <Tooltip title="Click to sort">
                        {getSortIcon('listPrice')}
                      </Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol 
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { backgroundColor: '#e0e0e0' },
                      userSelect: 'none'
                    }}
                    onClick={() => handleSort('LeadTime')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Lead Time
                      <Tooltip title="Click to sort">
                        {getSortIcon('LeadTime')}
                      </Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol 
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { backgroundColor: '#e0e0e0' },
                      userSelect: 'none'
                    }}
                    onClick={() => handleSort('description1')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Description
                      <Tooltip title="Click to sort">
                        {getSortIcon('description1')}
                      </Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol 
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { backgroundColor: '#e0e0e0' },
                      userSelect: 'none'
                    }}
                    onClick={() => handleSort('inStock')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      In Stock
                      <Tooltip title="Click to sort">
                        {getSortIcon('inStock')}
                      </Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol 
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { backgroundColor: '#e0e0e0' },
                      userSelect: 'none'
                    }}
                    onClick={() => handleSort('TCode')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      HTS Code
                      <Tooltip title="Click to sort">
                        {getSortIcon('TCode')}
                      </Tooltip>
                    </Box>
                  </TableCol>
                  <TableCol 
                    sx={{ 
                      cursor: 'pointer', 
                      '&:hover': { backgroundColor: '#e0e0e0' },
                      userSelect: 'none'
                    }}
                    onClick={() => handleSort('EXCOO')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Mfg Country of Origin
                      <Tooltip title="Click to sort">
                        {getSortIcon('EXCOO')}
                      </Tooltip>
                    </Box>
                  </TableCol>
                </TableRow>
              </TableHead>
              <TableBody>
                                 {hasFetchedLeadTime ? (
                   !leadTimeListPrice || displayData.length === 0 ? (
                    <TableRow>
                      <TableCol colSpan={11} sx={{ textAlign: 'center' }}>
                        No Data Found
                      </TableCol>
                    </TableRow>
                  ) : (
                      displayData.map((item: LeadTimeListPriceProps, idx: number) => (
                        <TableRow key={idx} hover>
                          {/* {showPartNoColumn && <TableCol>{item.partNo}</TableCol>} */}
                          <TableCol>{item.partNo}</TableCol>
                          <TableCol>
                            {item.listPrice ? (
                              `$ ${item.listPrice}`
                            ) : (
                              formatListPriceMessage(item.listPriceMessage)
                            )}
                          </TableCol>
                          <TableCol>
                            {item.LeadTime ? (
                              <span>{item.LeadTime} Working Days *</span>
                            ) : ''}
                          </TableCol>
                          <TableCol>
                             {item.description1 && item.description2 ? 
                               `${item.description1} (${item.description2})` : 
                               item.description1 || 'Description not available'
                             }
                           </TableCol>
                          {/* <TableCol>{item.inStock}
                            <Link
                              to={`/inventory/lead-time/search-result-who-has-it?partNo=${partNo}`}
                              color="#c3183c"
                              style={{
                                textDecoration: "underline",
                                color: "#c3183c",
                                cursor: "pointer",
                              }}
                            >
                              Who Has It?
                            </Link>
                          </TableCol> */}
                          <TableCol>
                            {item.inStock}
                            {item.inStock?.startsWith("Yes") && (
                              <Link
                                to={`/inventory/lead-time/search-result-who-has-it?partNo=${item.partNo}`}
                                style={{
                                  textDecoration: "underline",
                                  color: "#c3183c",
                                  marginLeft: 8,
                                  cursor: "pointer",
                                }}
                              >
                                Who Has It?
                              </Link>
                            )}
                          </TableCol>

                          <TableCol>{item.TCode}</TableCol>
                          <TableCol>{item.EXCOO}</TableCol>
                        </TableRow>
                      ))
                  )
                ) : (
                  <TableRow>
                    <TableCol colSpan={11}>
                      <Skeleton width="38%" />
                      <Skeleton width="100%" />
                      <Skeleton width="100%" />
                      <Skeleton width="61%" />
                    </TableCol>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {isExactSearch(partNo, leadTimeListPrice?.content) && (
                         <Typography variant="body1" gutterBottom sx={{ fontSize: 12, mt:2, p:2}}>
               * Lead Time only applies to quantities of {leadTimeListPrice?.content?.[0]?.OrderCap} or less for this item. For quantities <br/>
               greater than {leadTimeListPrice?.content?.[0]?.OrderCap} please call Customer Care at 800-835-1060.<br/>
               <br/>
               Prices quoted are subject to change due to tariff impact.
               {leadTimeListPrice?.content?.[0]?.MOQ && (
                 <>
                   <br/>
                   <br/>
                   MOQ for this item is: {leadTimeListPrice.content[0].MOQ}
                 </>
               )}
             </Typography>
          )}
        </Box>
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "flex-start"
          }}
        >
            <Button variant="contained" sx={{ backgroundColor: '#c3183c', padding: '7px 25px !important' }} 
            onClick={handleBack}>
              BACK
            </Button>
        </Box>

        {/* <RecentInventorySearch /> */}
    </DashboardLayout>
    </>
  )
};

export default LeadTimeSearchResult;